import { create } from 'zustand'
import { User } from '../types'
import client from '../api/client'
import { clearChatCache } from './chatStore'

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  setToken: (token: string) => void
  setAuthData: (accessToken: string, refreshToken: string, userData: any) => void
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,

  setToken: (token: string) => {
    localStorage.setItem('token', token)
    if (typeof token === 'string' && token.length > 0) {
      set({ token })
    }
  },

  setAuthData: (accessToken: string, refreshToken: string, userData: any) => {
    localStorage.setItem('token', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    localStorage.setItem('user', JSON.stringify(userData))
    set({ token: accessToken, user: userData })
  },

  login: async (email: string, password: string) => {
    const { data } = await client.post('/auth/login', { email, password })
    const token = data.access_token || data.token
    localStorage.setItem('token', token)
    if (data.refresh_token) {
      localStorage.setItem('refreshToken', data.refresh_token)
    }
    // Получаем профиль пользователя
    const me = await client.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
    localStorage.setItem('user', JSON.stringify(me.data))
    set({ user: me.data, token })
  },

  register: async (email: string, password: string, name: string) => {
    const { data } = await client.post('/auth/register', { email, password, name })
    const token = data.access_token || data.token
    localStorage.setItem('token', token)
    if (data.refresh_token) {
      localStorage.setItem('refreshToken', data.refresh_token)
    }
    set({ token })
    // Получаем профиль
    const me = await client.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
    set({ user: me.data })
  },

  logout: () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('cached_balance')
    clearChatCache()
    set({ user: null, token: null })
  },

  checkAuth: async () => {
    const token = get().token || localStorage.getItem('token')
    if (!token) {
      set({ isLoading: false })
      return
    }
    try {
      const { data } = await client.get('/auth/me')
      set({ user: data, token, isLoading: false })
    } catch {
      // Пробуем восстановить из localStorage
      const savedUser = localStorage.getItem('user')
      if (token && savedUser) {
        try {
          set({ user: JSON.parse(savedUser), token, isLoading: false })
          return
        } catch {}
      }
      // Пробуем обновить токен
      const refreshToken = localStorage.getItem('refreshToken')
      if (refreshToken) {
        try {
          const resp = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${refreshToken}`, 'Content-Type': 'application/json' },
          })
          if (resp.ok) {
            const data = await resp.json()
            const newToken = data.access_token || data.token
            localStorage.setItem('token', newToken)
            if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token)
            set({ token: newToken })
            // Повторная попытка получить профиль
            const me = await fetch('/api/auth/me', {
              headers: { 'Authorization': `Bearer ${newToken}` }
            })
            if (me.ok) {
              set({ user: await me.json(), isLoading: false })
              return
            }
          }
        } catch {}
      }
      // Если не смогли обновить — всё равно не удаляем токен из памяти
      // ProtectedRoute сам решит что делать
      set({ isLoading: false })
    }
  },
}))
